"use client";

import { useTranslations } from "next-intl";
import { FC, useEffect } from "react";

import useCoherenceLinksContext from "@/app/(main)/components/coherence_links_provider";
import { useCommentsFeedSafe } from "@/app/(main)/components/comments_feed_provider";
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/auth_context";
import { useHideCP } from "@/contexts/cp_context";
import { useBreakpoint } from "@/hooks/tailwind";
import { PostStatus, PostWithForecasts } from "@/types/post";
import cn from "@/utils/core/cn";

import { isDisplayableQuestionLink } from "../key_factors/utils";
import { shouldPostShowUserScores } from "../post_score_data/utils";
import { useQuestionLayout } from "../question_layout/question_layout_context";
import { hasGroupDistributions } from "../question_view/consumer_question_view/group_distribution_utils";
import { hasTimeline } from "../question_view/consumer_question_view/timeline";

type TabKey =
  | "comments"
  | "key-factors"
  | "info"
  | "news-hotness"
  | "my-scores"
  | "question-links"
  | "private-notes"
  | "timeline"
  | "distributions"
  | "similar-questions";

type TabDef = {
  key: TabKey;
  label: string;
  count?: number;
  indicator?: boolean;
};

type Props = {
  post: PostWithForecasts;
  variant: "consumer" | "forecaster";
  className?: string;
};

const tabClassName = (isActive: boolean) =>
  cn(
    "border-[1.25px] border-solid rounded-full font-medium transition-colors",
    "text-sm leading-4 px-2 py-1 sm:text-base sm:leading-6 sm:px-[15px] sm:py-[5px]",
    isActive
      ? "bg-blue-500/60 border-transparent text-blue-900 dark:bg-blue-500-dark/60 dark:text-blue-900-dark"
      : "bg-gray-0 border-blue-500/20 text-blue-900 dark:bg-gray-0-dark dark:border-blue-500-dark/20 dark:text-blue-900-dark"
  );

const QuestionPageShellTabBar: FC<Props> = ({ post, variant, className }) => {
  const t = useTranslations();
  const { activeTab, setActiveTab, privateNoteText } = useQuestionLayout();
  const { user } = useAuth();
  const isAdmin = !!(user?.is_staff || user?.is_superuser);
  const hasPrivateNote =
    (privateNoteText ?? post.private_note?.text ?? "").trim().length > 0;

  const isSm = useBreakpoint("sm");
  const { hideCP } = useHideCP();
  const commentsFeed = useCommentsFeedSafe();
  const commentCount =
    typeof commentsFeed?.totalCount === "number"
      ? commentsFeed.totalCount
      : post.comment_count ?? 0;
  const { aggregateCoherenceLinks } = useCoherenceLinksContext();
  const questionLinksCount = aggregateCoherenceLinks.data.filter(
    isDisplayableQuestionLink
  ).length;
  const keyFactorsCount = (post.key_factors?.length ?? 0) + questionLinksCount;
  const hasScores = shouldPostShowUserScores(post);
  const hasSimilarQuestionsTab =
    !isSm && post.curation_status === PostStatus.APPROVED;

  // Admin-only debugging tab for the "In the news" ranking, shown right after Info.
  const newsHotnessTab: TabDef[] = isAdmin
    ? [{ key: "news-hotness", label: t("newsHotness") }]
    : [];

  const forecasterTabs: TabDef[] = [
    { key: "comments", label: t("comments"), count: commentCount },
    { key: "key-factors", label: t("keyFactors"), count: keyFactorsCount },
    { key: "info", label: t("info") },
    ...newsHotnessTab,
    { key: "question-links", label: t("questionLinks") },
    {
      key: "private-notes",
      label: t("privateNotes"),
      indicator: hasPrivateNote,
    },
    ...(hasSimilarQuestionsTab
      ? [
          {
            key: "similar-questions" as TabKey,
            label: t("similarQuestions"),
          },
        ]
      : []),
  ];

  const consumerTabs: TabDef[] = [
    { key: "comments", label: t("comments"), count: commentCount },
    ...(!isSm && hasTimeline(post)
      ? [{ key: "timeline" as TabKey, label: t("timeline") }]
      : []),
    ...(!isSm && !hideCP && hasGroupDistributions(post)
      ? [{ key: "distributions" as TabKey, label: t("distributions") }]
      : []),
    { key: "key-factors", label: t("keyFactors"), count: keyFactorsCount },
    { key: "info", label: t("info") },
    ...newsHotnessTab,
    ...(hasScores
      ? [{ key: "my-scores" as TabKey, label: t("myScores") }]
      : []),
    ...(hasSimilarQuestionsTab
      ? [
          {
            key: "similar-questions" as TabKey,
            label: t("similarQuestions"),
          },
        ]
      : []),
  ];

  const tabs = variant === "forecaster" ? forecasterTabs : consumerTabs;

  const defaultValue: TabKey = "comments";
  const active =
    activeTab && tabs.some((tab) => tab.key === activeTab)
      ? activeTab
      : defaultValue;

  // Sync context when the active tab is dropped from the list (e.g. on resize).
  useEffect(() => {
    if (activeTab && active !== activeTab) {
      setActiveTab(active);
    }
  }, [active, activeTab, setActiveTab]);

  return (
    <Tabs
      defaultValue={defaultValue}
      value={active}
      onChange={setActiveTab}
      className={cn("bg-transparent dark:bg-transparent", className)}
    >
      <TabsList contained className="gap-[10px] overflow-x-auto">
        {tabs.map((tab) => (
          <TabsTab
            key={tab.key}
            value={tab.key}
            scrollOnSelect={false}
            dynamicClassName={tabClassName}
          >
            {tab.indicator ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2.5 shrink-0 rounded-full bg-orange-500 dark:bg-orange-500-dark" />
                <span>{tab.label}</span>
              </span>
            ) : tab.count !== undefined ? (
              `${tab.label} (${tab.count})`
            ) : (
              tab.label
            )}
          </TabsTab>
        ))}
      </TabsList>
    </Tabs>
  );
};

export default QuestionPageShellTabBar;
